import { z } from 'zod'

/**
 * Generic invitation form validation schema for email invitations
 */
export const invitationSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['SuperAdmin', 'Admin', 'Member']),
})

/**
 * Schema for user selection invitations
 */
export const userSelectionSchema = z.object({
  selectedUsers: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
        role: z.enum(['SuperAdmin', 'Admin', 'Member']),
      })
    )
    .min(1, 'At least one user must be selected'),
  defaultRole: z.enum(['SuperAdmin', 'Admin', 'Member']),
})

/**
 * Combined invitation schema supporting both email and user selection modes
 */
export const combinedInvitationSchema = z
  .object({
    mode: z.enum(['email', 'selection']),
    email: z.string().email('Invalid email address').optional(),
    role: z.enum(['SuperAdmin', 'Admin', 'Member']).optional(),
    selectedUsers: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          email: z.string(),
          role: z.enum(['SuperAdmin', 'Admin', 'Member']),
        })
      )
      .optional(),
    defaultRole: z.enum(['SuperAdmin', 'Admin', 'Member']).optional(),
  })
  .refine(
    (data) => {
      if (data.mode === 'email') {
        return data.email && data.role
      }
      if (data.mode === 'selection') {
        return data.selectedUsers && data.selectedUsers.length > 0 && data.defaultRole
      }
      return false
    },
    {
      message: 'Either email and role, or selected users and default role are required',
      path: ['mode'],
    }
  )

export type InvitationFormValues = z.infer<typeof invitationSchema>
export type UserSelectionFormValues = z.infer<typeof userSelectionSchema>
export type CombinedInvitationFormValues = z.infer<typeof combinedInvitationSchema>
